import React from 'react';
import { RefreshCw, Repeat } from 'lucide-react';
import { theme } from './theme';
import { useLanguage } from '../../components/LanguageContext';
import './portals.css';

// ============================================================
// Reusable chrome for the tenant / guest / EA portals:
//   - PortalHeader: title bar with an optional "switch role" action
//   - PortalBottomNav: role-colored bottom navigation
// ============================================================

interface HeaderProps {
  title: string;
  subtitle?: string;
  accent?: string;
  onChangeRole?: () => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  right?: React.ReactNode;
}

export const PortalHeader: React.FC<HeaderProps> = ({
  title, subtitle, accent = theme.gold, onChangeRole, onRefresh, refreshing, right,
}) => {
  const { t } = useLanguage();
  return (
  <header
    style={{
      background: '#fff',
      borderBottom: `1px solid ${theme.line}`,
      padding: '14px 16px calc(14px + env(safe-area-inset-top, 0px))',
      position: 'sticky',
      top: 0,
      zIndex: 40,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
    }}
  >
    <div style={{ minWidth: 0 }}>
      <h1 style={{ fontSize: 19, fontWeight: 800, color: theme.ink, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: 99, background: accent, display: 'inline-block' }} />
        {title}
      </h1>
      {subtitle && <p style={{ fontSize: 12.5, color: theme.slate, margin: '2px 0 0 16px' }}>{subtitle}</p>}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {right}
      {onRefresh && (
        <button
          onClick={onRefresh}
          title={t('pcRefresh')}
          aria-label={t('pcRefresh')}
          disabled={refreshing}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: '#f3f4f6', border: 'none', borderRadius: 10,
            padding: '8px 10px', color: theme.slate, cursor: 'pointer',
          }}
        >
          <RefreshCw size={15} className={refreshing ? 'pt-rotate' : undefined} />
        </button>
      )}
      {onChangeRole && (
        <button
          onClick={onChangeRole}
          title={t('pcChangeRole')}
          aria-label={t('pcChangeRole')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: '#f3f4f6', border: 'none', borderRadius: 10,
            padding: '8px 10px', color: theme.slate, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}
        >
          <Repeat size={14} />
        </button>
      )}
    </div>
  </header>
  );
};

export interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  /** Unread count on the tab, e.g. replies waiting in Messages. */
  badge?: number;
}

interface BottomNavProps {
  items: NavItem[];
  active: string;
  onChange: (id: string) => void;
  accent?: string;
}

export const PortalBottomNav: React.FC<BottomNavProps> = ({ items, active, onChange, accent = theme.gold }) => (
  <nav className="pt-bottom-nav">
    {items.map((item) => {
      const Icon = item.icon;
      const isActive = active === item.id;
      return (
        <button
          key={item.id}
          onClick={() => onChange(item.id)}
          className={`pt-nav-item ${isActive ? 'active' : ''}`}
          style={{ color: isActive ? accent : theme.slateLight }}
        >
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <Icon size={22} />
            {!!item.badge && item.badge > 0 && (
              <span className="pm-msg-dot" style={{ position: 'absolute', top: -6, right: -10 }}>
                {item.badge > 9 ? '9+' : item.badge}
              </span>
            )}
          </span>
          <span>{item.label}</span>
        </button>
      );
    })}
  </nav>
);
